import {
    getLocale as getUserLocale,
    getLocaleOverride,
} from '@eventbrite/i18n';
import pickBy from 'lodash/pickBy';
import {
    RefundPolicyCode,
    RefundPolicyCodeWithoutValidityDays,
} from '../../api/models/refundPolicy';
import { CaptchaStatus } from '../../models/app';
import {
    fallbackRefundPolicyValidityDaysByRefundPolicyCode,
    isRefundPolicyCodeWithValidityDays,
} from '../../models/refundPolicy';
import { TicketsPageSize } from '../../models/ticketsPage';
import { CheckoutState } from '../../state';
import { WithAppNamespace } from '../../state/app';

export { getIsAppRebrandActive } from './getIsAppRebrandActive';
export { getIsTicketSelectionCompactSizeFlow } from './getIsTicketSelectionCompactSizeFlow';
export { getPlatformLabel } from './getPlatformLabel';
export { shouldNavigateToTicketSelectionWhenClosingModal } from './shouldNavigateToTicketSelectionWhenClosingModal';

export const getDefaultUserCountry = ({ app }: CheckoutState): string =>
    app.defaultUserCountry;

// user's timezone computed from cloudfront request headers or TLD cookies
export const getCurrentTimezone = ({
    app,
}: CheckoutState): string | undefined => app.currentTimezone;

export const getEbDomain = ({ app }: CheckoutState) => app.ebDomain;

export const getActiveFFsFromCore = ({ app }: CheckoutState) => {
    return Object.keys(
        pickBy(app.featureFlags, (value: any) =>
            typeof value === 'object' ? value.isActive : !!value,
        ),
    ).sort();
};

export const getCheckoutUniversalFlag = ({ app }: CheckoutState) =>
    !!app.featureFlags?.checkoutUniversalFlag;

export const getCheckoutAppVersion = ({ app }: CheckoutState) => app.appVersion;

export const getAffiliateCode = ({ app }: CheckoutState) => app.affiliateCode;

export const getBackendSource = ({ app }: CheckoutState) => app.backend;

export const getFeatureFlags = ({ app }: CheckoutState) => app.featureFlags;

export const getIsModal = ({ app }: CheckoutState) => app.isModal;

export const getAppLocale = ({ app }: CheckoutState): string => app.locale;
export const getLocales = ({ app }: CheckoutState) => app.locales;
export const getDefaultLocale = ({ app, event }: CheckoutState) =>
    getLocaleOverride() ??
    getUserLocale({
        eventLocale: ('properties' in event && event.properties.locale) || '',
        appLocale: app.locale,
    });

export const getLocaleProps = (state: CheckoutState) => ({
    locales: getLocales(state),
    defaultLocale: getDefaultLocale(state),
});

export const isEventbriteTLD = ({ app }: CheckoutState): boolean =>
    app.isEventbriteTLD;

export const getGooglePayEnvironment = ({ app }: CheckoutState) => {
    return app.isProd ? 'PRODUCTION' : 'TEST';
};

export const getCurrencyFormat = ({ app }: CheckoutState) => app.currencyFormat;

export const getIsRegEvent = ({ app }: CheckoutState) => !!app.isRegEvent;

export const getTicketsPageSize = ({ app }: CheckoutState) =>
    (app.request?.params?.ticketsPageSize as TicketsPageSize) ??
    TicketsPageSize.full;

export const getIsEnablePaymentConstraintsInNewCreate = ({
    app,
}: CheckoutState) => !!app.featureFlags?.enablePaymentConstraintsInNewCreate;

export const getIsEnableEBMarketingOptInFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableEBMarketingOptIn;

export const getIsEnableEBMarketingOptInImplicitFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableEBMarketingOptInImplicit;

export const getIsEnableRefundFeeRetentionPolicyFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableRefundFeeRetentionPolicy;

export const getIsEnableNonceBraintreePaymentsFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableNonceBraintreePayments;

export const getMaxInstallmentsFF = ({ app }: CheckoutState) =>
    app.featureFlags?.maxInstallments;

export const getIsEnableSquareCreditCardCheckoutFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableSquareCreditCardCheckout;

export const getShouldHideSeriesStartTimeFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.shouldHideSeriesStartTime;

export const getLaunchPayinsPaymentMethodPaypalFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.launchPayinsPaymentMethodPaypal;

export const getIsOrganizationExcludedFromPostCheckoutMandatoryQuestions = ({
    app,
}: CheckoutState) =>
    !!app.featureFlags?.postCheckoutMandatoryQuestionsOrganizationsExclusions;

export const getIsEnableTimedEntryFF = ({ app }: Pick<CheckoutState, 'app'>) =>
    !!app.featureFlags?.enableTimedEntry;

export const getEnableNewRebrandingLogoAnimationFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableNewRebrandingLogoAnimation;

export const getAreEventbriteLegacyPaymentMethodsDisabled = ({
    app,
}: CheckoutState) => !!app.featureFlags?.disableEventbriteLegacyPaymentMethods;

export const getIsStripeElementsInAndroidNativeEnabled = ({
    app,
}: CheckoutState) => !!app.featureFlags?.enableStripeElementsInAndroidNative;

export const getIsStripeElementsInIosNativeEnabled = ({ app }: CheckoutState) =>
    !!app.featureFlags?.enableStripeElementsInIosNative;

export const getIsProd = ({ app }: CheckoutState) => !!app.isProd;

export const isIosApp = <State extends WithAppNamespace>({ app }: State) =>
    app.useIosProfile;

export const isAndroidApp = <State extends WithAppNamespace>({ app }: State) =>
    app.useAndroidProfile;

export const isNativeApp = <State extends WithAppNamespace>({ app }: State) =>
    !!(app.useIosProfile || app.useAndroidProfile);

export const hasFewTicketsLeftUrgencySignal = ({ app }: CheckoutState) =>
    !!app.urgencySignals?.messages.fewTickets;

export const hasSalesEndSoonUrgencySignal = ({ app }: CheckoutState) =>
    !!app.urgencySignals?.messages.salesEndSoon;

export const getCorrelationId = ({ app }: CheckoutState) =>
    app.request.correlation_id;

export const getSessionId = ({ app }: CheckoutState) => app.request.session_id;

export const getWaitlistCode = ({ app }: CheckoutState) => app.waitlistCode;

export const getWidgetParentUrl = ({ app }: CheckoutState) =>
    app.widgetParentUrl;

export const getInviteToken = ({ app }: CheckoutState) => app.inviteToken;

export const getCampaingToken = ({ app }: CheckoutState) => app.campaignToken;

export const getInviteEmail = ({ app }: CheckoutState) => app.inviteEmail;

export const getCountryWithPostalCode = ({ app }: CheckoutState) =>
    app.countryWithPostalCode;

export const isMyOrderFlow = ({ app }: CheckoutState) => !!app?.isMyOrderFlow;

export const isPayinsFlow = ({ app }: CheckoutState) =>
    !!app.payins?.isPayinsFlow;

export const getExistingOrderId = ({ app }: CheckoutState) =>
    app.existingOrderId;

export const getIsMobile = ({ app }: CheckoutState) => app.isMobile;

export const getReloadAfterCheckout = ({ app }: CheckoutState) =>
    !!app.reloadAfterCheckout;

export const getWasExternalPaymentWindowBlocked = ({ app }: CheckoutState) =>
    !!app.wasExternalPaymentWindowBlocked;

export const getRefundPolicyCode = ({ app }: CheckoutState) =>
    app.refundPolicyCode;

export const getRefundPolicyValidityDays = ({ app }: CheckoutState) =>
    app.refundPolicyValidityDays;

export const getRefundPolicySettings = ({
    app,
}: CheckoutState):
    | {
          refundPolicyCode: Exclude<
              RefundPolicyCode,
              RefundPolicyCodeWithoutValidityDays
          >;
          refundPolicyValidityDays: number;
      }
    | {
          refundPolicyCode: RefundPolicyCodeWithoutValidityDays;
          refundPolicyValidityDays: number | null;
      } => {
    const { refundPolicyCode, refundPolicyValidityDays } = app;

    return isRefundPolicyCodeWithValidityDays(refundPolicyCode)
        ? {
              refundPolicyCode,
              refundPolicyValidityDays:
                  refundPolicyValidityDays ??
                  fallbackRefundPolicyValidityDaysByRefundPolicyCode[
                      refundPolicyCode
                  ],
          }
        : {
              refundPolicyCode,
              refundPolicyValidityDays,
          };
};

export const getEventIdFromApp = ({ app }: CheckoutState) => app.event.id;

export const getRebrandingStatus = ({ app }: CheckoutState) =>
    app?.request?.params?.rebranding_status;

export const getCaptchaSettings = ({ app }: CheckoutState) => app?.captcha;

export const getDefaultPayinAmount = ({ app }: CheckoutState) =>
    app?.request?.params?.payin_amount;

export const getCaptchaV3Token = ({ app }: CheckoutState) =>
    app?.captchaV3?.token;

export const getCaptchaV3Status = ({ app }: CheckoutState) =>
    app?.captchaV3?.status;

export const getIsCaptchaV3Initialized = ({ app }: CheckoutState) =>
    [CaptchaStatus.loading, CaptchaStatus.loaded].includes(
        app?.captchaV3?.status,
    );

export const getCaptchaV3TimeoutReached = ({ app }: CheckoutState) =>
    app?.captchaV3?.timeoutReached;

export const getRequestPath = ({ app }: CheckoutState) => app.request?.path;

export const getGuestAgeAndId = ({ app }: CheckoutState) => ({
    guestAge: app.guestAge,
    guestId: app.guestId,
});

export const getGuestAge = ({ app }: CheckoutState) => app.guestAge;

export const getRequestExtraMetadata = ({ app }: CheckoutState) => ({
    ip: app.requestExtraData?.ip,
    host: app.requestExtraData?.host,
    httpReferer: app.requestExtraData?.httpReferer,
    method: app.requestExtraData?.method,
});

export const getRecaptcha3PublicKey = ({ app }: CheckoutState) =>
    app?.captcha?.recaptcha3PublicKey;

export const getSeriesInfiniteCalendarFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.seriesInfiniteCalendar;

export const getSeriesInfiniteCalendarDiscountFF = ({ app }: CheckoutState) =>
    !!app.featureFlags?.series_infinite_calendar_discounts;
